// Registry of authored transition sequences.
//
// Adding a sequence means: write a score (a `SequenceScore` in `src/theme/`),
// write a renderer that subscribes to the clock and paints its own DOM, then add
// one entry here. Nothing in TransitionHost, the store, or RouteLayout needs to
// change — that generalization was the whole point of splitting the original
// all-in-one TapeTransition apart.
//
// `onStart` / `onCommit` exist so a sequence can own its own audio and haptics
// without the host knowing anything about either. The host wraps both in
// try/catch: garnish must never be able to abort the run.

import type { ComponentType } from 'react';
import type { MotionTier } from '../../theme/motion';
import type { SequenceScore } from '../../theme/sequence';
import { useTransitionStore, type TransitionKind } from '../../stores/transition';
import { buildTapeTimeline } from '../../theme/tapeTimeline';
import { buildSpliceScore } from '../../theme/spliceTimeline';
import { buildAdvanceScore } from '../../theme/advanceTimeline';
import { TapeTransportRenderer } from './TapeTransportRenderer';
import { SpliceRenderer } from './SpliceRenderer';
import { AdvanceRenderer } from './AdvanceRenderer';
import { sfx } from '../../audio/sfx';
import { haptics } from '../../audio/haptics';

export interface SequenceEntry<S extends SequenceScore = SequenceScore> {
  /** Human-readable, for logs and the dev scrub. */
  label: string;
  buildScore: (tier: MotionTier) => S;
  Renderer: ComponentType<{ score: S }>;
  /** Fired once when the sequence begins. */
  onStart?: () => void;
  /** Fired once on the impact frame, alongside the route commit. */
  onCommit?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each entry is
// internally consistent (its score type matches its renderer); the map itself
// is necessarily heterogeneous.
type AnyEntry = SequenceEntry<any>;

export const SEQUENCES: Record<TransitionKind, AnyEntry> = {
  tape: {
    label: 'tape transport (welcome → menu)',
    buildScore: buildTapeTimeline,
    Renderer: TapeTransportRenderer,
    onStart: () => {
      sfx.tapeTransport();
      haptics.tap();
    },
    onCommit: () => {
      sfx.tapeLock();
      haptics.impact(7);
    },
  },

  splice: {
    label: 'tape splice (tavern → combat)',
    // Familiarity is decided by the caller and passed through the store's
    // payload — the round number lives in the match store, which the sequence
    // layer deliberately knows nothing about.
    buildScore: (tier) =>
      buildSpliceScore(tier, {
        familiar: useTransitionStore.getState().payload.familiar === '1',
      }),
    Renderer: SpliceRenderer,
    // The stop, then the slam. Cutting sound on the freeze is as much of the
    // idea as cutting picture — `tapeLock` is the mechanism jamming, and the
    // silence after it is what makes the impact land.
    onStart: () => {
      sfx.tapeLock();
      haptics.tap();
    },
    onCommit: () => {
      sfx.tapeSplice();
      haptics.impact(9);
    },
  },

  advance: {
    label: 'reel advance (step → step)',
    buildScore: (tier) =>
      buildAdvanceScore(tier, {
        reverse: useTransitionStore.getState().payload.reverse === '1',
      }),
    Renderer: AdvanceRenderer,
    // Deliberately quiet. This punctuates a step the player takes two or three
    // times per run; a stinger here would wear out immediately. Just the click
    // they already expect from pressing a button, and a light haptic.
    onStart: () => {
      sfx.click();
      haptics.tap();
    },
  },
};
